// ============================================================================
// issuePhotoService.ts
// Client-side photo compression + upload/delete for routine_check_items
// ============================================================================

const MAX_DIMENSION = 1600;          // resize so longest side <= 1600px
const JPEG_QUALITY  = 0.8;            // 0..1 JPEG quality
const MAX_PHOTOS    = 5;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export interface UploadResult {
  success: true;
  path: string;          // e.g. uploads/routine-photos/123/45_171...e3.jpg
  url: string;           // e.g. /uploads/routine-photos/123/45_171...e3.jpg
  photos: string[];      // updated full array
}

export interface UploadError {
  success: false;
  error: string;
}

/**
 * Compress an image File on the client side.
 * Returns a Blob ready for upload.
 */
export async function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      reject(new Error('Unsupported file type'));
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const { width: w0, height: h0 } = img;
        const scale = Math.min(1, MAX_DIMENSION / Math.max(w0, h0));
        const w = Math.round(w0 * scale);
        const h = Math.round(h0 * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context unavailable');
        ctx.drawImage(img, 0, 0, w, h);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
              reject(new Error('Compression failed'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Upload a single photo for a given check item.
 */
export async function uploadIssuePhoto(
  checkId: number,
  itemId: number,
  file: File
): Promise<UploadResult | UploadError> {
  try {
    const blob = await compressImage(file);
    const compressedFile = new File([blob], `photo-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });

    const fd = new FormData();
    fd.append('check_id', String(checkId));
    fd.append('item_id', String(itemId));
    fd.append('photo', compressedFile);

    const res = await fetch('/api/routine-check-photo-upload.php', {
      method: 'POST',
      body: fd,
    });

    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || `HTTP ${res.status}` };
    }
    return json as UploadResult;
  } catch (e: any) {
    return { success: false, error: e?.message || 'Upload failed' };
  }
}

/**
 * Delete a previously uploaded photo.
 */
export async function deleteIssuePhoto(
  itemId: number,
  path: string
): Promise<{ success: boolean; photos?: string[]; error?: string }> {
  try {
    const res = await fetch('/api/routine-check-photo-upload.php', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_id: itemId, path }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || `HTTP ${res.status}` };
    }
    return { success: true, photos: json.photos };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Delete failed' };
  }
}

export const PHOTO_LIMITS = {
  MAX_PHOTOS,
  MAX_DIMENSION,
  JPEG_QUALITY,
  ALLOWED_TYPES,
};
